import { store } from "@/store";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/**
 * User fields for GTM payloads from Redux. Safe when singleCurrentUser is null
 * (e.g. before hydration).
 */
export function getTrackUserContext(): {
  channel: string;
  product: string;
  userId: string | undefined;
} {
  const u = store.getState().users.singleCurrentUser;
  const businessList = store.getState().constants.bunchAllBusinessList.list as {
    id: string;
    name: string;
  }[];
  const productList = store.getState().constants.bunchAllProductList.list as {
    id: string;
    name: string;
  }[];
  return {
    channel: businessList.find((b) => b.id === u?.business)?.name ?? "",
    product: productList.find((p) => p.id === u?.team)?.name ?? "",
    userId: u?.name,
  };
}

// Debug mode is opt-in via env only.
// This avoids accidentally marking all events as debug when builds use non-production modes.
const DEBUG_MODE = import.meta.env.VITE_GA_DEBUG_MODE === "true";

/**
 * Dynamically load the GTM container if an ID is configured.
 * Keeps the container ID in env (not hardcoded in HTML) and
 * allows different containers per environment.
 */
export function initGtm(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const containerId = import.meta.env.VITE_GTM_CONTAINER_ID;
  // If no container is configured for this environment, do not load GTM.
  if (!containerId) return;

  // Avoid loading GTM multiple times
  if (document.getElementById("gtm-script")) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });
  const firstScript = document.getElementsByTagName("script")[0];
  const gtmScript = document.createElement("script");
  gtmScript.id = "gtm-script";
  gtmScript.async = true;
  gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  firstScript.parentNode?.insertBefore(gtmScript, firstScript);
}

function getDataLayer(): Record<string, unknown>[] {
  if (typeof window === "undefined") return [];
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

/**
 * Push an object to the GTM dataLayer. GTM can use these for triggers.
 */
export function pushToDataLayer(payload: Record<string, unknown>): void {
  try {
    getDataLayer().push(payload);
  } catch {
    // dataLayer push must never throw to callers.
  }
}

/**
 * Send a custom event to the dataLayer (e.g. for GTM event triggers).
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean | undefined>
): void {
  try {
    const action = params?.Action ?? params?.action;
    const category = params?.Category ?? params?.category;
    const location = params?.Location ?? params?.location;
    const entity = params?.Entity ?? params?.entity;
    const channel = params?.Channel ?? params?.channel ?? "web";
    const product = params?.Product ?? params?.product ?? "slike";
    const userId = params?.User_ID ?? params?.user_id ?? params?.userId;

    const payload: Record<string, unknown> = {
      event: eventName,
      ...(action !== undefined ? { Action: action } : {}),
      ...(category !== undefined ? { Category: category } : {}),
      ...(location !== undefined ? { Location: location } : {}),
      ...(entity !== undefined ? { Entity: entity } : {}),
      Channel: channel,
      Product: product,
      ...(userId !== undefined ? { User_ID: userId } : {}),
      ...(DEBUG_MODE ? { debug_mode: true } : {}),
    };
    const handledKeys = new Set([
      "Action",
      "Category",
      "Location",
      "Entity",
      "Channel",
      "Product",
      "User_ID",
    ]);
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && !handledKeys.has(k)) payload[k] = v;
      });
    }
    pushToDataLayer(payload);
  } catch {
    // Analytics must never block UI or primary handlers.
  }
}

/**
 * Track "create" action when a create-option button is clicked (video, shorts, custom upload, embed, bucket, etc.).
 * Pushes to dataLayer so GTM can fire tags (e.g. GA4 event).
 *
 * @param location - Which create option: "video" | "shorts" | "custom_upload" | "embed" | "audio" | "drive" | "slates" | "campaign" | "simulive" | bucket name
 */
export function trackCreate(location: string): void {
  pushToDataLayer({
    event: "upload",
    category: "create",
    action: "create",
    entity: location,
    Action: "create",
    Category: "create",
    Location: "create",
    Entity: location,
    Channel: "web",
    Product: "slike",
    ...(DEBUG_MODE ? { debug_mode: true } : {}),
  });
}
